import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trash2, Plus, Minus, Utensils, ChefHat, Gift, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { dishImg, onDishImgError } from "../lib/dishImage";

const ALLERGEN_CHIPS = ["Gluten", "Dairy", "Nuts", "Peanuts", "Shellfish", "Eggs", "Fish", "Soy", "Sesame"];

export default function CustomerCart() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { items, inc, dec, remove, total, restaurantId, clear, tableNo: ctxTable, setTableNo, appendOrderId, setAppendOrderId } = useCart();
  const { user } = useAuth();
  const [tableNo, setTableNoLocal] = useState(ctxTable || "");
  const [placing, setPlacing] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [orderAllergens, setOrderAllergens] = useState([]);

  useEffect(() => { if (ctxTable) setTableNoLocal(ctxTable); }, [ctxTable]);
  // Pre-fill from the user's saved profile preferences when they first land on the cart.
  useEffect(() => {
    const pref = user?.preferences?.allergens;
    if (Array.isArray(pref) && pref.length && orderAllergens.length === 0) {
      setOrderAllergens(pref.filter((a) => ALLERGEN_CHIPS.includes(a)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const conflicts = useMemo(() => {
    const flagged = orderAllergens.map((a) => a.toLowerCase());
    return items.filter((it) => (it.allergens || []).some((a) => flagged.includes(String(a).toLowerCase())));
  }, [items, orderAllergens]);

  const toggleAllergen = (a) => setOrderAllergens((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try { const { data } = await api.get(`/customer/loyalty/${slug}`); setLoyalty(data); }
      catch { /* noop */ }
    })();
  }, [user, slug]);

  const isAppending = Boolean(appendOrderId);
  const discount = loyalty?.eligible && !isAppending ? total * (loyalty.discount_pct || 0) / 100 : 0;
  const afterDiscount = Math.max(total - discount, 0);
  const tax = afterDiscount * 0.05;
  const serviceFee = afterDiscount * 0.05;
  const grand = afterDiscount + tax + serviceFee;

  const place = async () => {
    if (!items.length) return;
    if (!tableNo.trim()) { toast.error("Please enter your table number"); return; }
    setPlacing(true);
    try {
      setTableNo(slug, tableNo.trim());
      const orderItems = items.map((i) => ({ item_id: i.item_id, qty: i.qty, notes: i.notes }));
      let data;
      if (isAppending) {
        ({ data } = await api.post(`/customer/orders/${appendOrderId}/append`, {
          restaurant_id: restaurantId,
          table_no: tableNo.trim(),
          items: orderItems,
          allergen_flags: orderAllergens,
        }));
        toast.success("Added to your running order — kitchen notified");
        setAppendOrderId(slug, null);
      } else {
        ({ data } = await api.post("/customer/orders", {
          restaurant_id: restaurantId,
          table_no: tableNo.trim(),
          items: orderItems,
          allergen_flags: orderAllergens,
        }));
        toast.success("Your order is on the way to the kitchen");
      }
      clear();
      nav(`/r/${slug}/order/${data.id}/success`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order");
    } finally { setPlacing(false); }
  };

  if (!items.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: "var(--bg)", color: "var(--fg)" }}>
        <Utensils className="w-10 h-10" style={{ color: "var(--accent)" }} />
        <h2 className="font-display text-3xl mt-4">Your cart is resting</h2>
        <p className="mt-2" style={{ color: "var(--fg-muted)" }}>Pick a dish and we'll get the kitchen moving.</p>
        <Link to={`/r/${slug}`} className="mt-6 gold-btn rounded-full px-6 py-3 font-semibold" data-testid="back-to-menu">Browse menu</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      <header className="glass sticky top-0 z-30 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <Link to={`/r/${slug}`} className="p-2 rounded-full hover:opacity-80" data-testid="cart-back" style={{ background: "var(--glass-light)" }}><ArrowLeft className="w-5 h-5" /></Link>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: "var(--fg-muted)" }}>Your table</div>
            <div className="font-display text-xl">Send to kitchen</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <section className="space-y-3" data-testid="cart-items">
          {items.map((it) => (
            <motion.div key={it.item_id} layout className="flex gap-3 p-3 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
              <img src={dishImg(it)} onError={onDishImgError} alt={it.name} className="w-20 h-20 rounded-xl object-cover" />
              <div className="flex-1">
                <div className="font-display text-lg">{it.name}</div>
                <div className="text-xs font-mono" style={{ color: "var(--fg-muted)" }}>₹{it.price} × {it.qty}</div>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={() => dec(it.item_id)} className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80" style={{ border: "1px solid var(--border-strong)", background: "var(--glass-light)" }} data-testid={`dec-${it.item_id}`}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-6 text-center">{it.qty}</span>
                  <button onClick={() => inc(it.item_id)} className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80" style={{ border: "1px solid var(--border-strong)", background: "var(--glass-light)" }} data-testid={`inc-${it.item_id}`}><Plus className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(it.item_id)} className="ml-auto text-xs hover:text-red-400 flex items-center gap-1" style={{ color: "var(--fg-muted)" }} data-testid={`rm-${it.item_id}`}><Trash2 className="w-3.5 h-3.5" /> remove</button>
                </div>
              </div>
              <div className="font-mono gold-text self-start">₹{(it.price * it.qty).toFixed(0)}</div>
            </motion.div>
          ))}
        </section>

        <section className="p-4 rounded-2xl" style={{ border: `1px solid ${conflicts.length ? "rgba(239,68,68,0.5)" : "var(--border)"}`, background: "var(--bg-card)" }} data-testid="order-allergens">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 mt-0.5" style={{ color: conflicts.length ? "#ef4444" : "var(--accent)" }} />
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Tell the kitchen about your allergies</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--fg-muted)" }}>Tap any that apply for this order — we'll flag them on the kitchen ticket.</div>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap mt-3">
            {ALLERGEN_CHIPS.map((a) => {
              const on = orderAllergens.includes(a);
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleAllergen(a)}
                  data-testid={`cart-al-${a}`}
                  style={{
                    background: on ? "var(--accent)" : "transparent",
                    color: on ? "var(--accent-fg)" : "var(--fg)",
                    border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                  }}
                  className="text-xs px-3 py-1 rounded-full"
                >{a}</button>
              );
            })}
          </div>
          {conflicts.length > 0 && (
            <div className="mt-3 p-2.5 rounded-lg text-xs" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5" }} data-testid="allergy-conflict">
              <b>Heads up:</b> {conflicts.map((c) => c.name).join(", ")} contain{conflicts.length === 1 ? "s" : ""} something on your list. Remove from cart or pick alternatives if needed.
            </div>
          )}
        </section>

        <section className="p-4 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Table</div>
          <input
            value={tableNo}
            onChange={(e) => setTableNoLocal(e.target.value)}
            data-testid="table-input"
            className="mt-1 w-full bg-transparent border-b py-2 font-display text-2xl focus:outline-none"
            style={{ borderColor: "var(--border)", color: "var(--fg)" }}
            placeholder="Table number — scan QR or enter manually"
          />
        </section>

        <section className="p-4 rounded-2xl flex gap-3 items-start" style={{ border: "1px solid rgba(229,169,60,0.25)", background: "var(--bg-card)" }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(229,169,60,0.15)", border: "1px solid rgba(229,169,60,0.35)", color: "var(--accent)" }}><ChefHat className="w-4 h-4" /></div>
          <div className="text-sm leading-relaxed" style={{ color: "var(--fg-muted)" }}>
            <span className="font-semibold" style={{ color: "var(--accent)" }}>Dine &amp; pay at your pace.</span> Your order goes straight to the kitchen. You'll pay the bill at the end of your meal — just tap <b style={{ color: "var(--fg)" }}>Call Waiter</b> whenever you're ready.
          </div>
        </section>

        <section className="p-4 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
          <Row label="Subtotal" value={`₹${total.toFixed(0)}`} />
          {loyalty?.eligible && discount > 0 && (
            <Row
              label={<span style={{ color: "var(--accent)" }}>Loyalty discount ({loyalty.discount_pct}%)</span>}
              value={<span style={{ color: "var(--accent)" }}>-₹{discount.toFixed(0)}</span>}
            />
          )}
          <Row label="GST (5%)" value={`₹${tax.toFixed(0)}`} />
          <Row label="Service (5%)" value={`₹${serviceFee.toFixed(0)}`} />
          <div className="h-px my-2" style={{ background: "var(--border)" }} />
          <Row label={<span className="font-display text-xl">Total</span>} value={<span className="font-display text-xl gold-text">₹{grand.toFixed(0)}</span>} />
        </section>

        {loyalty?.eligible && (
          <section
            className="p-4 rounded-2xl flex gap-3 items-start"
            style={{ border: "1px solid var(--accent)", background: "linear-gradient(to right, rgba(229,169,60,0.12), transparent)" }}
            data-testid="loyalty-notify"
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}><Gift className="w-4 h-4" /></div>
            <div className="text-sm leading-relaxed" style={{ color: "var(--fg-muted)" }}>
              <span className="font-semibold" style={{ color: "var(--accent)" }}>Regular perks applied to this order.</span>
              {loyalty.discount_pct > 0 && <> You're saving <b style={{ color: "var(--fg)" }}>₹{discount.toFixed(0)}</b> ({loyalty.discount_pct}% off).</>}
              {loyalty.complimentary_item && <> We'll add a complimentary <b style={{ color: "var(--fg)" }}>{loyalty.complimentary_item.name}</b> to your table — on the house.</>}
            </div>
          </section>
        )}

        <div className="pt-2">
          <button onClick={place} disabled={placing || !user} data-testid="place-order-btn" className="w-full gold-btn rounded-full py-4 font-semibold text-base disabled:opacity-50">
            {!user ? "Sign in with phone to place order" : (placing ? (isAppending ? "Adding to your order…" : "Sending to the kitchen…") : `${isAppending ? "Add to running order" : "Send to kitchen"} — ₹${grand.toFixed(0)}`)}
          </button>
          {!user && (
            <Link to="/login/customer" state={{ from: `/r/${slug}/cart` }} className="block text-center mt-3 text-xs hover:underline" style={{ color: "var(--accent)" }} data-testid="cart-login-link">
              Use OTP to sign in →
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm" style={{ color: "var(--fg-muted)" }}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
